import React, { useRef, useEffect, useState } from "react";
import { Chip, Modal, Box, Typography, IconButton, Avatar, Grid, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import soundFile from "../../Raw/stopped.mp3";
import ConfirmationEscortModal from "./ConfirmationEscortModal";
import apiClient from "Utils/apiClient";

const UserConnected = ({ user, userId, escort, onOpenEscortReport, isTripActive = true }) => {
  const audio = useRef(new Audio(soundFile));
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(180);
  const [openEscortConfirmModal, setEscortConfirmModal] = useState(false);
  const hasModalOpened = useRef(false);
  const countdownInterval = useRef(null);
  const now = Date.now();
  const rawTimestamp = Number(user?.currentTimeStamp || user?.timestamp || user?.lastMovedAt || 0);
  const isRecentTelemetry = rawTimestamp > 0 && (now - rawTimestamp < 60000);
  const status = Boolean(
    user?.socketId ||
    user?.online_status ||
    user?.isOnline ||
    isRecentTelemetry
  );

  const hasSubmittedLater = escort?.submitLatter === true;

  //console.log("Getting User Id", user, userId);

  const handleSubmitLater = async () => {
    try {
      const targetUserId = escort?.userId || userId;

      if (!targetUserId) {
        throw new Error("Missing user id for submit later");
      }

      await apiClient.put(`/users/${targetUserId}`, {
        submitLatter: true,
      });

      setOpen(false);
      setEscortConfirmModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isTripActive) {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
      setOpen(false);
      audio.current.pause();
      audio.current.currentTime = 0;
      hasModalOpened.current = false;
      return;
    }

    if (hasSubmittedLater) {
      setOpen(false);
      return;
    }

    if (status) {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
        countdownInterval.current = null;
      }
      setOpen(false);
      setCountdown(180);
      audio.current.pause();
      audio.current.currentTime = 0;
      hasModalOpened.current = false;
      return;
    }

    if (hasModalOpened.current) return;

    const disconnectDelay = setTimeout(() => {
      if (!status && !hasModalOpened.current && isTripActive) {
        setOpen(true);
        hasModalOpened.current = true;
        audio.current.play()?.catch((e) => console.warn(e));

        countdownInterval.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval.current);
              setOpen(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }, 25000);

    return () => clearTimeout(disconnectDelay);
  }, [status, hasSubmittedLater, isTripActive]);

  const getColor = () => {
    if (!isTripActive) {
      return { color: "#64748b", text: "ENDED" };
    }
    return status ? { color: "#1b5e20", text: "ON" } : { color: "#d50000", text: "OFF" };
  };

  const color = getColor();

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleEscortConfirmationModalClose = () => {
    setEscortConfirmModal(false);
  };

  const handleSubmit = () => {
    setOpen(false);
    setEscortConfirmModal(false);

    if (typeof onOpenEscortReport === "function") {
      onOpenEscortReport(escort || { userData: user, userId });
    }
  };

  const closeAllModals = () => {
    setOpen(false);
    setEscortConfirmModal(false);
  };

  return ( 
    <>
      <Chip
        label={<div style={{ textAlign: "center" }}>{color.text}</div>}
        style={{
          backgroundColor: color.color,
          color: "white",
          borderRadius: "5px",
          fontWeight: "bold",
          height: "auto",
          padding: "5px",
        }}
      />

      <Modal
        open={Boolean(isTripActive && open)}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setOpen(false);
          }
        }}
        disableEscapeKeyDown
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 3,
            p: 4,
            width: 400,
            maxWidth: "90%",
          }}
        >
          <IconButton
            onClick={() => {
              setEscortConfirmModal(true);
            }}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="body1" color="error" gutterBottom>
            🚨 {user?.userName || "User"} Lost on Radar During Active Escort
          </Typography>

          <Box display="flex" justifyContent="center">
            <Typography variant="h1" color="primary" sx={{ mb: 1 }}>
              {formatTime(countdown)}
            </Typography>
          </Box>

          <Divider sx={{ mb: 0, bgcolor: "secondary.light" }} />

          <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
            User Info
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Avatar src={user?.userImage} alt={user?.userName} sx={{ width: 56, height: 56 }} />
            </Grid>
            <Grid item xs>
              <Typography variant="body2">
                <strong>Name:</strong> {user?.userName || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Mobile:</strong> {user?.userPhone || "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1, bgcolor: "secondary.light" }} />

          <Typography variant="body2">
            <strong>Action Required:</strong>
            <br />
            Admin staff must immediately initiate a wellbeing check on the user:
            <ul>
              <li>Send a message via the chat platform.</li>
              <li>If no response in 2–3 minutes, proceed to the next step.</li>
              <li>Attempt to contact the user by phone.</li>
              <li>Confirm whether the phone is ringing or switched off.</li>
              <li>
                If the user cannot be reached, escalate immediately: Dispatch to VigilantEye to
                perform a physical check based on the user’s last known GPS location.
              </li>
            </ul>
          </Typography>
        </Box>
      </Modal>

      <ConfirmationEscortModal
        open={openEscortConfirmModal}
        handleClose={handleEscortConfirmationModalClose}
        handleSubmit={handleSubmit}
        username={user?.userName || "User"}
        userId={userId}
        onCloseMainModal={closeAllModals}
        submitLatter={handleSubmitLater}
      />
    </>
  );
};

export default React.memo(UserConnected);
